<template>
    <v-dialog
        :value="isOpen"
        max-width="600px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="!isNewStopCard">
                        Karta stop <strong>{{ stopCard.number }}</strong>
                    </template>
                    <template v-else>
                        Tworzenie karty stop
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <v-autocomplete
                    ref="objectNumberField"
                    v-model="objectNumber"
                    :items="objectNumbers"
                    label="Numer zlecenia lub zamówienia"
                    hint="Wpisz lub zeskanuj numer zlecenia lub zamówienia"
                    :loading="objectNumberSearchLoading"
                    :search-input.sync="objectNumberSearchQuery"
                    return-object
                    no-data-text="Brak danych..."
                    item-text="reportItemPrimaryNumber"
                    item-value="reportItemPrimaryNumber"
                    :error-messages="objectNumberErrors"
                    :disabled="!isNewStopCard"
                    outlined
                    dense
                    autofocus
                    @change="updateStopCardObjectNumber()"
                >
                    <template slot="no-data">
                        <v-list-item>
                            <v-list-item-title>
                                <template v-if="!objectNumberSearchQuery">
                                    Wpisz treść aby odnaleźć <strong>zamówienie lub zlecenie</strong>.
                                </template>
                                <template v-else-if="objectNumberSearchQuery.length < objectNumberSearchMinQueryLength">
                                    Wpisz przynajmniej <strong>{{ objectNumberSearchMinQueryLength }}</strong> znaki.
                                </template>
                                <template v-else-if="actionLoading.objectNumberSearchLoading">
                                    Trwa wyszukiwanie zamówienia/zlecenia...
                                </template>
                                <template v-else>
                                    Nie odnaleziono zamówienia/zlecenia na zapytanie: "<strong> {{ objectNumberSearchQuery }}".</strong>
                                </template>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                </v-autocomplete>
                <v-select
                    v-model="stopCard.plateNumber"
                    :items="objectNumbers"
                    type="number"
                    label="Numer montażu"
                    item-text="plateNumber"
                    item-value="plateNumber"
                    outlined
                    dense
                    :error-messages="plateNumberErrors"
                    :disabled="!isNewStopCard"
                />
                <v-text-field
                    v-model="stopCard.quantity"
                    type="number"
                    label="Ilość wstrzymana"
                    :error-messages="quantityErrors"
                    outlined
                    dense
                    :disabled="!isNewStopCard"
                />
                <v-select
                    v-model="stopCard.productionDepartmentId"
                    label="Dział"
                    :items="stopCardDepartments"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono działu"
                    :error-messages="productionDepartmentIdErrors"
                    outlined
                    dense
                    required
                    :disabled="!isNewStopCard"
                />
                <v-select
                    v-model="stopCard.productionProcessId"
                    label="Proces"
                    :items="productionProcesses"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono działu"
                    :error-messages="productionProcessIdErrors"
                    outlined
                    dense
                    required
                    :disabled="!isNewStopCard"
                />
                <v-select
                    v-model="stopCard.errorId"
                    label="Kod błędu"
                    :items="errors"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono działu"
                    :error-messages="errorIdErrors"
                    outlined
                    dense
                    required
                    :disabled="!isNewStopCard"
                />
                <v-select
                    v-model="stopCard.productTypeId"
                    :items="productTypes"
                    item-text="name"
                    item-value="id"
                    label="Produkt"
                    no-data-text="Brak danych..."
                    clearable
                    :error-messages="productTypeIdErrors"
                    outlined
                    dense
                    :disabled="!isNewStopCard"
                />
                <v-textarea
                    v-model="stopCard.description"
                    label="Opis niezgodności"
                    name="Opis niezgodności"
                    outlined
                    dense
                    required
                    :disabled="!isNewStopCard"
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="actionLoading"
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isNewStopCard"
                    :loading="actionLoading"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="saveStopCard"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { required } from 'vuelidate/lib/validators'
import { isGreaterThanZero } from '@/utils/customValidators'
import decodeQRcode from '@/mixins/decodeQRcode'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

let searchTimer

export default {
    name: 'StopCardDialog',
    mixins: [
        decodeQRcode
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        stopCardData: {
            type: Object,
            default: () => {}
        }

    },
    data() {
        return {
            stopCard: {
                jobNumber: null,
                orderNumber: null,
                plateNumber: '',
                quantity: null,
                errorId: null,
                productionDepartmentId: null,
                productionProcessId: null,
                description: null,
                productTypeId: null
            },
            objectNumberSearchQuery: null,
            objectNumber: null,
            actionLoading: false,
            objectNumberSearchLoading: false,
            objectNumberSearch: null,
            objectNumberSearchMinQueryLength: 2,
            objectNumbers: []
        }
    },
    validations() {
        const validations = {
            objectNumber: {
                required
            },
            stopCard: {
                quantity: {
                    required
                },
                plateNumber: {},
                errorId: {
                    required
                },
                productionDepartmentId: {
                    required
                },
                productionProcessId: {
                    required
                },
                productTypeId: {
                    required
                }
            }
        }

        if (this.stopCard.jobNumber !== null && this.stopCard.jobNumber !== '') {
            validations.stopCard.plateNumber = {
                required,
                isGreaterThanZero
            }
        }

        return validations
    },
    computed: {
        isNewStopCard() {
            return isEmpty(this.stopCardData)
        },
        errors() {
            return this.$store.state.complaints.stopCards.errors
        },
        productionProcesses() {
            return this.$store.state.configurations.productProductionProcesses.productionProcesses
        },
        stopCardDepartments() {
            return this.$store.state.administration.users.stopCardProductionDepartments
        },
        productTypes() {
            if (this.objectNumber && this.objectNumber.products?.length > 0) {
                const productIds = this.objectNumber.products.map(product => product.id)

                return this.$store.state.configurations.products.products.filter(
                    product => productIds.includes(product.id)
                )
            }
            
            return this.$store.state.configurations.products.products
        },
        // Walidacje
        plateNumberErrors() {
            const errors = []

            if (!this.$v.stopCard.plateNumber.$dirty) return errors
            this.$v.stopCard.plateNumber.required === false && errors.push('Proszę podać numer montażu.')

            if (
                this.$v.stopCard.plateNumber?.$invalid &&
                this.$v.stopCard.plateNumber?.isGreaterThanZero === false
            )
                errors.push('Proszę podać numer montażu (większy niż 0).')

            return errors
        },
        quantityErrors() {
            const errors = []

            if (!this.$v.stopCard.quantity.$dirty) return errors
            !this.$v.stopCard.quantity.required && errors.push('Proszę podać ilość wstrzymaną.')

            return errors
        },
        errorIdErrors() {
            const errors = []

            if (!this.$v.stopCard.errorId.$dirty) return errors
            !this.$v.stopCard.errorId.required && errors.push('Proszę wybrać kod błędu.')

            return errors
        },
        productionDepartmentIdErrors() {
            const errors = []

            if (!this.$v.stopCard.productionDepartmentId.$dirty) return errors
            !this.$v.stopCard.productionDepartmentId.required && errors.push('Proszę wybrać dział.')

            return errors
        },
        productionProcessIdErrors() {
            const errors = []

            if (!this.$v.stopCard.productionProcessId.$dirty) return errors
            !this.$v.stopCard.productionProcessId.required && errors.push('Proszę wybrać proces.')

            return errors
        },
        productTypeIdErrors() {
            const errors = []

            if (!this.$v.stopCard.productTypeId.$dirty) return errors
            !this.$v.stopCard.productTypeId.required && errors.push('Proszę wybrać produkt.')

            return errors
        },
        objectNumberErrors() {
            const errors = []

            if (!this.$v.objectNumber.$dirty) return errors
            !this.$v.objectNumber.required && errors.push('Proszę wybrać numer zlecenia lub zamówienia.')

            return errors
        }
    },
    watch: {
        isOpen(value) {
            if (value && !this.isNewStopCard) {
                this.stopCard = JSON.parse(JSON.stringify(this.stopCardData))
                this.stopCard.plateNumber = +this.stopCard.plateNumber
                this.objectNumber = this.stopCard.jobNumber ? JSON.parse(JSON.stringify(this.stopCard.jobNumber)) : JSON.parse(JSON.stringify(this.stopCard.orderNumber))
                this.searchObjectNumberSearch(this.objectNumber)
            }
        },
        objectNumberSearchQuery(value = null) {
            if (value === null || value.length < this.objectNumberSearchMinQueryLength || !this.isNewStopCard) return

            this.objectNumberSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchObjectNumberSearch(value)
                } else {
                    this.objectNumberSearchLoading = false
                }
            }, 500)
        },
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        updateStopCardObjectNumber() {
            if ([10, 30].includes(this.objectNumber.reportItemTypeId)) {
                this.stopCard.jobNumber = this.objectNumber.reportItemPrimaryNumber
                this.stopCard.orderNumber = null
            }

            if (this.objectNumber.reportItemTypeId === 15) {
                this.stopCard.orderNumber = this.objectNumber.reportItemPrimaryNumber
                this.stopCard.jobNumber = null
            }
        },
        searchObjectNumberSearch(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('complaints/getReportItemPlates', {
                paginate: '?limit=20',
                query: `&reportItemPrimaryNumber=${query}`
            })
                .then(response => {
                    this.objectNumbers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.objectNumberSearchLoading = false
                })
        },
        handleQrJSON({ data }) {
            if (this.isNewStopCard) {
                if (data.type === 'KZ') {
                    this.objectNumber = data.taskNumber
                    this.searchObjectNumberSearch(data.taskNumber)
                } else if (data.type === 'KP') {
                    this.objectNumber = data.order
                    this.searchObjectNumberSearch(data.order)
                }
                else {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: this.$i18n.t('reports.messages.unsupportedQrCode')
                    })
                }
            }
        },
        saveStopCard() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.actionLoading = true
                this.$store.dispatch('complaints/createStopCard', this.stopCard)
                    .then(() => {
                        this.$emit('on-save')
                        this.$v.$reset()
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading = false
                        this.onClose()
                    })
            }
        },
        onClose() {
            setTimeout(() => {
                this.stopCard = {
                    jobNumber: null,
                    orderNumber: null,
                    plateNumber: '',
                    quantity: null,
                    errorId: null,
                    productionDepartmentId: null,
                    productionProcessId: null,
                    description: null,
                    productTypeId: null
                }
                this.objectNumbers = []
                this.$v.$reset()
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>
